// schedule.ts — Patch 010.A.4 R7 split. Schedule + groundbreak methods.

import { request } from './request'
import type {
  CreateScheduleTaskInput,
  GroundbreakUpdateInput,
  LookaheadResponse,
  MaterializeResponse,
  RecomputeResponse,
  ScheduleProject,
  ScheduleResponse,
  ScheduleTask,
  SubReadinessResponse,
} from './types'

export const scheduleApi = {
  get: (projectId: string, includeSuperseded = false) =>
    request<ScheduleResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule${
        includeSuperseded ? '?include_superseded=true' : ''
      }`,
    ),
  createTask: (projectId: string, body: CreateScheduleTaskInput) =>
    request<{ ok: true; task: ScheduleTask; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/tasks`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  completeTask: (projectId: string, taskId: string) =>
    request<{ ok: true; task: ScheduleTask; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/tasks/${encodeURIComponent(
        taskId,
      )}/complete`,
      { method: 'PATCH', body: JSON.stringify({}) },
    ),
  uncompleteTask: (projectId: string, taskId: string) =>
    request<{ ok: true; task: ScheduleTask; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/tasks/${encodeURIComponent(
        taskId,
      )}/uncomplete`,
      { method: 'PATCH', body: JSON.stringify({}) },
    ),
  updateGroundbreak: (projectId: string, body: GroundbreakUpdateInput) =>
    request<{ ok: true; project: ScheduleProject; request_id: string }>(
      `/api/projects/${encodeURIComponent(projectId)}/groundbreak`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  recompute: (projectId: string) =>
    request<RecomputeResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/recompute`,
      { method: 'POST' },
    ),
  // Patch 015.4 — universe->schedule materialization from the latest cascade run.
  materialize: (projectId: string) =>
    request<MaterializeResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/materialize`,
      { method: 'POST' },
    ),
  lookahead: (projectId: string, days = 14) =>
    request<LookaheadResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/lookahead?days=${days}`,
    ),
  // Sub-readiness: for upcoming phases, is the sub lined up + insured for the
  // work? Joins the dated calendar to the roster + COI/W-9 loop. Feeds the
  // Today cockpit gate.
  subReadiness: (projectId: string, horizon = 45) =>
    request<SubReadinessResponse>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/sub-readiness?horizon=${horizon}`,
    ),
  // Patch 016.1.W — delay a task by N days + record reason. Pushes
  // planned_start/finish + triggers recompute so downstream shifts.
  delayTask: (
    projectId: string,
    taskId: string,
    body: { days: number; reason: string },
  ) =>
    request<{
      ok: true
      task_id: string
      new_planned_start: string
      new_planned_finish: string
      days_delayed: number
      reason: string
      recompute_ok: boolean
      recompute_error?: string
      request_id: string
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/tasks/${encodeURIComponent(taskId)}/delay`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // Patch 016.1.W — tasks past planned_start with no actual_start.
  lateTasks: (projectId: string) =>
    request<{
      ok: true
      tasks: Array<{
        id: string
        display_name: string
        phase: string | null
        schedule_lane: string | null
        status: string
        planned_start: string | null
        planned_finish: string | null
        actual_start: string | null
        weather_sensitive: number
        is_on_critical_path: number
        buffer_days: number
        blocked_by_predecessor_count: number
        notes: string | null
        days_late: number
      }>
      count: number
      today: string
    }>(
      `/api/projects/${encodeURIComponent(projectId)}/schedule/late-tasks`,
    ),
}
